import React from 'react';
import { CartesianGrid, ComposedChart, Line, ResponsiveContainer, Scatter, Tooltip, XAxis, YAxis } from 'recharts';
import { formatDate, formatDuration, formatPace, SOURCE_LABELS } from '../utils/format';
import { axisFor, Y_AXIS } from '../utils/axis';
import { ChartPoint, DistanceSeries, timeAxisTicks } from '../utils/progression';

interface Props {
  series: DistanceSeries[];
  view: 'yearly' | 'pb';
  /** Opens one distance in the single-distance chart. */
  onOpen: (code: string) => void;
}

const AXIS = { stroke: 'var(--text-muted)', fontSize: 11 };

function TileTooltip({ active, payload }: { active?: boolean; payload?: Array<{ payload: ChartPoint }> }) {
  const point = active ? payload?.find(p => p.payload?.effort)?.payload : undefined;
  if (!point) return null;
  return (
    <div className="progress-tip">
      <strong>{formatDuration(point.effort.timeS)}</strong> · {formatPace(point.effort.paceSPerKm)}
      <div>{formatDate(point.effort.date)}</div>
      <div className="muted">
        {point.effort.activityName ?? 'Manual time'} · {SOURCE_LABELS[point.effort.source]}
      </div>
    </div>
  );
}

/**
 * One small chart per distance on a shared time axis, each with its own finishing-time axis. Nothing overlaps, so the
 * shape of every distance's progress reads at a glance. Press a tile to open that distance on its own.
 */
const ProgressSmallMultiples: React.FC<Props> = ({ series, view, onOpen }) => {
  const xs = series.flatMap(s => [...s.dots, ...s.line].map(p => p.x));
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const xAxis = timeAxisTicks(minX, maxX);

  return (
    <div className="progress-grid" data-testid="progress-grid">
      {series.map(s => {
        const ys = [...s.dots.map(p => p.y), ...s.line.map(p => p.y), ...(s.trend ? [s.trend.y1, s.trend.y2] : [])];
        const yAxis = axisFor(ys, 'time', 4);
        const latest = s.line[s.line.length - 1];
        const behind = latest ? Math.round((latest.effort.timeS / s.bestTimeS - 1) * 1000) / 10 : 0;
        return (
          <section key={s.code} className="progress-tile">
            <button type="button" className="progress-tile-head" onClick={() => onOpen(s.code)} aria-label={`Open ${s.name}`}>
              <span className="progress-swatch" style={{ background: s.style.color }} aria-hidden="true" />
              <strong>{s.name}</strong>
              <span className="muted small ms-auto tabular">PB {formatDuration(s.bestTimeS)}</span>
            </button>
            <div className="small muted mb-1">
              {behind === 0 ? 'Latest result is the PB' : `Latest ${behind}% off the PB`}
              {s.trend && (
                <>
                  {' · '}
                  {s.trend.perYear < 0 ? 'getting faster' : 'getting slower'}
                </>
              )}
            </div>
            <div className="progress-tile-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart margin={{ top: 6, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke="var(--chart-grid)" />
                  <XAxis
                    type="number"
                    dataKey="x"
                    domain={[minX, maxX]}
                    ticks={xAxis.ticks}
                    tickFormatter={xAxis.format}
                    tick={AXIS}
                    tickMargin={3}
                  />
                  <YAxis
                    {...Y_AXIS}
                    fontSize={11}
                    type="number"
                    dataKey="y"
                    reversed
                    domain={yAxis ? yAxis.domain : ['auto', 'auto']}
                    ticks={yAxis?.ticks}
                    tickFormatter={v => formatDuration(Number(v))}
                    width={52}
                    interval={0}
                  />
                  <Tooltip content={<TileTooltip />} cursor={false} />
                  <Line
                    data={s.line}
                    dataKey="y"
                    stroke={s.style.color}
                    strokeWidth={2.5}
                    type={view === 'pb' ? 'stepAfter' : 'linear'}
                    dot={false}
                    activeDot={false}
                    isAnimationActive={false}
                  />
                  {s.trend && (
                    <Line
                      data={[
                        { x: s.trend.x1, y: s.trend.y1 },
                        { x: s.trend.x2, y: s.trend.y2 },
                      ]}
                      dataKey="y"
                      stroke={s.style.color}
                      strokeWidth={1.5}
                      strokeDasharray="2 5"
                      dot={false}
                      activeDot={false}
                      isAnimationActive={false}
                      tooltipType="none"
                    />
                  )}
                  <Scatter
                    data={s.dots}
                    dataKey="y"
                    fill={s.style.color}
                    stroke="var(--surface-1)"
                    isAnimationActive={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </section>
        );
      })}
    </div>
  );
};

export default ProgressSmallMultiples;
